import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState, fmtDate } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Target as TargetIcon, Plus, Globe, HardDrives } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Targets() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ workspace_id: "", name: "", value: "", type: "ip_range", description: "" });
  const canManage = user.role === "admin" || user.role === "pentester";

  const load = () => {
    api.get("/targets").then((r) => setItems(r.data));
    api.get("/workspaces").then((r) => setWorkspaces(r.data));
  };
  useEffect(() => { load(); }, []);

  const wsName = (id) => workspaces.find((w) => w.id === id)?.name || "—";

  const create = async () => {
    try {
      await api.post("/targets", form);
      toast.success("Target added");
      setOpen(false);
      setForm({ workspace_id: "", name: "", value: "", type: "ip_range", description: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="fade-up">
      <PageHeader
        overline="In-scope assets"
        title="Targets"
        subtitle="IP ranges and hostnames authorized for assessment within each workspace."
        action={
          canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-target-button" className="bg-blue-600 hover:bg-blue-500 text-white">
                  <Plus size={17} weight="bold" className="mr-1.5" /> Add Target
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0b1220] border-[#1e293b] text-white">
                <DialogHeader><DialogTitle>Add Target</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-slate-300">Workspace</Label>
                    <Select value={form.workspace_id} onValueChange={(v) => setForm({ ...form, workspace_id: v })}>
                      <SelectTrigger data-testid="target-workspace-select" className="mt-1 bg-[#0f172a] border-[#1e293b]">
                        <SelectValue placeholder="Select workspace" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0b1220] border-[#1e293b] text-white">
                        {workspaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Name</Label>
                    <Input data-testid="target-name-input" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1 bg-[#0f172a] border-[#1e293b]" placeholder="DMZ Range" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-300">Type</Label>
                      <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                        <SelectTrigger data-testid="target-type-select" className="mt-1 bg-[#0f172a] border-[#1e293b]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0b1220] border-[#1e293b] text-white">
                          <SelectItem value="ip_range">IP Range</SelectItem>
                          <SelectItem value="hostname">Hostname</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Value</Label>
                      <Input data-testid="target-value-input" value={form.value}
                        onChange={(e) => setForm({ ...form, value: e.target.value })}
                        className="mt-1 bg-[#0f172a] border-[#1e293b] font-mono" placeholder="10.0.0.0/24" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300">Description</Label>
                    <Textarea data-testid="target-desc-input" value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="mt-1 bg-[#0f172a] border-[#1e293b]" />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="target-create-submit" onClick={create}
                    disabled={!form.workspace_id || !form.name || !form.value}
                    className="bg-blue-600 hover:bg-blue-500 text-white">Add Target</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={TargetIcon} title="No targets" hint="Add an IP range or hostname within an authorized workspace." />
      ) : (
        <Card className="bg-[#0f172a] border-[#1e293b] rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-[#1e293b] hover:bg-transparent">
                <TableHead className="overline text-slate-500">Target</TableHead>
                <TableHead className="overline text-slate-500">Value</TableHead>
                <TableHead className="overline text-slate-500">Type</TableHead>
                <TableHead className="overline text-slate-500">Workspace</TableHead>
                <TableHead className="overline text-slate-500">Added</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`target-row-${t.id}`} className="border-[#1e293b] hover:bg-slate-800/40">
                  <TableCell className="text-white font-medium">{t.name}</TableCell>
                  <TableCell className="font-mono text-blue-300">{t.value}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-slate-400 text-sm">
                      {t.type === "hostname" ? <Globe size={15} /> : <HardDrives size={15} />}
                      {t.type === "hostname" ? "Hostname" : "IP Range"}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-400">{wsName(t.workspace_id)}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{fmtDate(t.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
